import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useI18n } from "../../../i18n/useI18n";
import { RootState } from "../../../../app/store";
import { closeCreateUpdateSection, showCreateUpdateSection } from "../../slices/projectsSlice";
import { useCreateSection } from "../../hooks/useCreateSection";
import { useUpdateSection } from "../../hooks/useUpdateSection";
import { useGetAllProjectSections } from "../../hooks/useGetAllProjectSections";
import { ISectionFormData } from "../../../../api/projects/types";

import Modal from "@/components/UI/Modal/Modal";
import Button from "@/components/UI/Button/Button";
import { Loader } from "../../../users/components/Loader";
import { ErrorMessage } from "../../../../components/Notification/ErrorMessage";

import detailProjectStyles from '../../styles/detailproject.module.scss';
import formStyles from '../../../users/styles/userForm.module.scss';

interface PropsModalSprintCreate {
  idproject?: string;
}

const ModalSectionCreate = ({ idproject }: PropsModalSprintCreate) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const modalState = useSelector((state: RootState) => state.projects.sectionModalState);
  const { mutateAsync: createSectionMutation, isPending: isPendingCreate } = useCreateSection();
  const { mutateAsync: updateSection, isPending: isPendingUpdate } = useUpdateSection();
  const [errorMessage, setErrorMessage] = useState("");
  const { refetch } = useGetAllProjectSections(Number(idproject));
  const isPending = isPendingCreate || isPendingUpdate;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ISectionFormData>();

  useEffect(() => {
    if (modalState?.show) {
      if (modalState?.sectionData) {
        reset({
          name: modalState.sectionData.title || "",
          description: modalState.sectionData.description || "",
          idProject: Number(idproject),
        });
      } else {
        reset({ name: "", description: "", idProject: Number(idproject) });
      }
    }
  }, [modalState, idproject, reset]);

  const handlerCloseModal = () => {
    dispatch(closeCreateUpdateSection());
    setErrorMessage("");
    reset();
  };

  const handleFormSubmit = async (data: ISectionFormData) => {
    try {
      if (modalState?.id !== undefined) {
        // Actualizar sección existente
        await updateSection({ sectionId: Number(modalState.id), formData: data }, {
          onSuccess: () => {
            refetch();
            handlerCloseModal();
          },
          onError: (error: Error) => {
            setErrorMessage(error.message || "Ocurrió un error. Inténtalo de nuevo.");
          },
        });
      } else {
        // Crear nueva sección
        await createSectionMutation(data, {
          onSuccess: () => {
            refetch();
            handlerCloseModal();
          },
          onError: (error: Error) => {
            setErrorMessage(error.message || "Ocurrió un error. Inténtalo de nuevo.");
          },
        });
      }
    } catch (error) {
      setErrorMessage("Error al ejecutar la acción en la sección: " + error);
    }
  };

  const modalFooter = (
    <>
      <Button variant="secondary" onClick={handlerCloseModal} disabled={isPending}>
        {t("module_users.modal_create_button_cancel")}
      </Button>
      <Button type="submit" form="section-form" variant="primary" disabled={isPending}>
        {isPending
          ? (modalState?.id ? t("module_projects.modal_updating_section_button") : t("module_projects.modal_creating_section_button"))
          : (modalState?.id ? t("module_projects.modal_update_section_button") : t("module_projects.modal_create_sprint_button"))
        }
      </Button>
    </>
  );

  return (
    <Fragment>
      <button
        className={detailProjectStyles["optionDetails__button-create-projects"]}
        onClick={() => dispatch(showCreateUpdateSection({}))}
      >
        {t("module_projects.modal_create_sprint_button")}
      </button>

      <Modal
        isOpen={!!modalState?.show}
        onClose={handlerCloseModal}
        title={
          modalState?.id !== undefined
            ? t("module_projects.modal_update_section_title")
            : t("module_projects.modal_create_sprint_button")
        }
        footer={modalFooter}
      >
        <form id="section-form" onSubmit={handleSubmit(handleFormSubmit)} className={formStyles.formGrid}>
          {isPending && <Loader />}
          <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
            <label htmlFor="name" className={formStyles.label}>
              {t("module_projects.modal_create_section_placeholder")}
            </label>
            <input
              id="name"
              className={formStyles.input}
              {...register("name", { required: t("module_projects.modal_create_section_error") })}
              placeholder={t("module_projects.modal_create_section_placeholder")}
            />
            {errors.name && <p className={formStyles.errorMessage}>{errors.name.message}</p>}
          </div>

          <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
            <label htmlFor="description" className={formStyles.label}>
              {t("module_projects.modal_create_section_description")}
            </label>
            <textarea
              id="description"
              className={formStyles.input}
              {...register("description", { required: t("module_projects.modal_create_section_error_description") })}
              placeholder={t("module_projects.modal_create_section_description")}
            />
            {errors.description && <p className={formStyles.errorMessage}>{errors.description.message}</p>}
          </div>
          
          {errorMessage && <ErrorMessage message={errorMessage} />}
        </form>
      </Modal>
    </Fragment>
  );
};

export default ModalSectionCreate;
